import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TestTube, Link2, Unlink, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';

type GmailStatus = {
  connected: boolean;
  configured: boolean;
  email: string | null;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  contactsScopeGranted?: boolean;
  includeOtherContacts?: boolean;
  contactsWriteEnabled?: boolean;
  lastContactsSyncAt?: string | null;
  lastContactsSyncError?: string | null;
};

type GmailConfig = {
  googleClientId: string;
  googleRedirectUri: string;
  hasClientSecret: boolean;
  fromName: string;
  syncIntervalMinutes: number;
  configured: boolean;
  suggestedRedirectUri: string;
  includeOtherContacts?: boolean;
  contactsWriteEnabled?: boolean;
  aiDisclosureMessage?: string;
};

function withToken(init: RequestInit = {}): RequestInit {
  const token = localStorage.getItem('accessToken');
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  };
}

export function GmailEmailSettingsSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<GmailStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [fromName, setFromName] = useState('Studio Gori');
  const [syncMinutes, setSyncMinutes] = useState(10);
  const [hasSecret, setHasSecret] = useState(false);
  const [suggestedRedirect, setSuggestedRedirect] = useState('');
  const [includeOtherContacts, setIncludeOtherContacts] = useState(false);
  const [contactsWriteEnabled, setContactsWriteEnabled] = useState(false);
  const [aiDisclosureMessage, setAiDisclosureMessage] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch('/api/gmail/config', withToken());
      const data = await res.json();
      if (data.success && data.data) {
        const c = data.data as GmailConfig;
        setClientId(c.googleClientId || '');
        setRedirectUri(c.googleRedirectUri || c.suggestedRedirectUri || '');
        setSuggestedRedirect(c.suggestedRedirectUri || '');
        setFromName(c.fromName || 'Studio Gori');
        setSyncMinutes(c.syncIntervalMinutes ?? 10);
        setHasSecret(c.hasClientSecret);
        setIncludeOtherContacts(c.includeOtherContacts ?? false);
        setContactsWriteEnabled(c.contactsWriteEnabled ?? false);
        setAiDisclosureMessage(c.aiDisclosureMessage ?? '');
      } else if (!res.ok) {
        toast.error(data.message || 'Errore caricamento configurazione Gmail');
      }
    } catch {
      toast.error('Errore caricamento configurazione Gmail');
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/gmail/oauth/status', withToken());
      const data = await res.json();
      if (data.success) {
        setStatus(data.data);
      } else {
        toast.error(data.message || 'Errore caricamento stato Gmail');
        if (data.data) setStatus(data.data);
      }
    } catch {
      toast.error('Errore caricamento stato Gmail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
    void loadStatus();
  }, [loadConfig, loadStatus]);

  useEffect(() => {
    const oauth = searchParams.get('oauth');
    if (!oauth) return;
    if (oauth === 'success') {
      toast.success('Gmail collegato con successo');
      void loadStatus();
    } else if (oauth === 'access_denied') {
      toast.error(
        'Google ha rifiutato l\'accesso (403). In Google Cloud → Schermata consenso OAuth → Utenti di test aggiungi l\'email Gmail con cui stai accedendo (es. titolare), poi riprova Collega.',
        { duration: 15000 }
      );
    } else if (oauth === 'error') {
      toast.error('Collegamento Gmail fallito');
    } else if (oauth === 'invalid_state') {
      toast.error('Sessione OAuth scaduta: riprova Collega account');
    }
    searchParams.delete('oauth');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, loadStatus]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        '/api/gmail/config',
        withToken({
          method: 'PUT',
          body: JSON.stringify({
            googleClientId: clientId,
            googleClientSecret: clientSecret || undefined,
            googleRedirectUri: redirectUri,
            fromName,
            syncIntervalMinutes: syncMinutes,
            includeOtherContacts,
            contactsWriteEnabled,
            aiDisclosureMessage,
          }),
        })
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Configurazione Google salvata');
        setClientSecret('');
        setHasSecret(true);
        await loadConfig();
        await loadStatus();
      } else {
        toast.error(data.message || 'Salvataggio fallito');
      }
    } catch {
      toast.error('Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    if (!status?.configured && !hasSecret) {
      toast.error('Salva prima Client ID e Client Secret da Google Cloud');
      return;
    }
    setConnecting(true);
    try {
      const res = await fetch('/api/gmail/oauth/start', withToken());
      const data = await res.json();
      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else {
        toast.error(data.message || 'Impossibile avviare OAuth');
      }
    } catch {
      toast.error('Errore connessione Gmail');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const res = await fetch('/api/gmail/oauth/disconnect', withToken({ method: 'POST' }));
      const data = await res.json();
      if (data.success) {
        toast.success('Gmail disconnesso');
        void loadStatus();
      } else {
        toast.error(data.message || 'Errore disconnessione');
      }
    } catch {
      toast.error('Errore disconnessione');
    }
  };

  const handleTest = async () => {
    try {
      const res = await fetch('/api/gmail/oauth/status', withToken());
      const data = await res.json();
      if (data.success && data.data?.connected) {
        toast.success(`Connessione attiva: ${data.data.email}`);
      } else if (data.success && data.data?.configured) {
        toast.message('App Google configurata, ma casella non ancora collegata');
      } else {
        toast.error(data.message || 'Completa la configurazione Google e collega la casella');
      }
    } catch {
      toast.error('Test fallito');
    }
  };

  const handleSyncProbe = async () => {
    try {
      const res = await fetch('/api/emails/sync', withToken({ method: 'POST' }));
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Sync completata');
        void loadStatus();
      } else {
        toast.error(data.message || 'Sync fallita');
      }
    } catch {
      toast.error('Sync fallita');
    }
  };

  const applySuggestedRedirect = () => {
    if (suggestedRedirect) setRedirectUri(suggestedRedirect);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>App Google Cloud (OAuth)</CardTitle>
          <CardDescription>
            Dopo aver creato il progetto su Google Cloud, incolla qui ID client, segreto e URI di
            reindirizzamento. Non serve modificare file <code className="text-xs">.env</code> sul
            server.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Guida passo-passo:{' '}
            <a
              href="/docs/GMAIL-SETUP-LATO-GOOGLE.md"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              GMAIL-SETUP-LATO-GOOGLE.md
            </a>{' '}
            (repository) oppure chiedi al team dev il PDF.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="gmail-client-id">Client ID OAuth</Label>
              <Input
                id="gmail-client-id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="123456789-xxxx.apps.googleusercontent.com"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="gmail-client-secret">Client Secret</Label>
              <Input
                id="gmail-client-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={hasSecret ? '•••••••• (lascia vuoto per non cambiare)' : 'GOCSPX-...'}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="gmail-redirect">URI di reindirizzamento (deve coincidere con Google)</Label>
              <Input
                id="gmail-redirect"
                value={redirectUri}
                onChange={(e) => setRedirectUri(e.target.value)}
                placeholder="https://tuodominio.it/api/gmail/oauth/callback"
              />
              {suggestedRedirect && suggestedRedirect !== redirectUri && (
                <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={applySuggestedRedirect}>
                  Usa suggerito: {suggestedRedirect}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="gmail-from-name">Nome mittente (opzionale)</Label>
              <Input id="gmail-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gmail-sync-min">Sync automatica (minuti)</Label>
              <Input
                id="gmail-sync-min"
                type="number"
                min={1}
                max={120}
                value={syncMinutes}
                onChange={(e) => setSyncMinutes(parseInt(e.target.value, 10) || 10)}
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-sm font-medium">Rubrica Google (People API)</p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeOtherContacts}
                onChange={(e) => setIncludeOtherContacts(e.target.checked)}
              />
              Includi altri contatti Google (oltre alla rubrica salvata)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={contactsWriteEnabled}
                onChange={(e) => setContactsWriteEnabled(e.target.checked)}
              />
              Consenti scrittura rubrica su Google (azione esplicita «Pubblica» in app)
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gmail-ai-disclosure">Messaggio di trasparenza AI (ogni email)</Label>
            <p className="text-xs text-muted-foreground">
              Aggiunto in calce a <strong>ogni</strong> email in uscita dallo studio (UI, fatture, template e
              messaggi automatici), non solo al primo contatto come su WhatsApp. Serve a dichiarare che il
              messaggio può essere generato da un sistema di intelligenza artificiale (obbligo di trasparenza,
              art. 50 AI Act). Lascia vuoto per non inviarlo.
            </p>
            <Textarea
              id="gmail-ai-disclosure"
              rows={5}
              value={aiDisclosureMessage}
              onChange={(e) => setAiDisclosureMessage(e.target.value)}
            />
          </div>

          <Button onClick={handleSaveConfig} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva configurazione Google'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Collegamento casella Gmail</CardTitle>
          <CardDescription>
            Casella condivisa del titolare. Collega una sola volta (solo ADMIN); tutti usano la
            pagina Email dell&apos;app. Se Google mostra 403, aggiungi l&apos;email del titolare in
            Google Cloud → Utenti di test (vedi guida).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Caricamento stato...</p>
          ) : status?.connected ? (
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 space-y-2">
              <p className="text-sm font-medium text-green-900">
                Collegato come <span className="font-mono">{status.email}</span>
              </p>
              {status.lastSyncAt && (
                <p className="text-xs text-muted-foreground">
                  Ultima sync: {new Date(status.lastSyncAt).toLocaleString('it-IT')}
                </p>
              )}
              {status.lastSyncError && (
                <p className="text-xs text-red-600">Errore sync: {status.lastSyncError}</p>
              )}
              {status.contactsScopeGranted === false && (
                <p className="text-xs text-red-700 mt-2">
                  Rubrica: ambito contatti mancante. Disconnetti e ricollega accettando tutti i permessi.
                </p>
              )}
              {status.lastContactsSyncAt && (
                <p className="text-xs text-muted-foreground">
                  Ultimo sync rubrica: {new Date(status.lastContactsSyncAt).toLocaleString('it-IT')}
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
              <p className="text-sm text-amber-900">
                {status?.configured
                  ? 'App Google configurata. Premi «Collega account Gmail» e accedi con la casella del titolare.'
                  : 'Salva prima la configurazione Google Cloud sopra, poi collega la casella.'}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!status?.connected ? (
              <Button onClick={handleConnect} disabled={connecting}>
                <Link2 className="h-4 w-4 mr-2" />
                {connecting ? 'Reindirizzamento...' : 'Collega account Gmail'}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleSyncProbe}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sincronizza ora
                </Button>
                <Button variant="outline" onClick={handleDisconnect}>
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnetti
                </Button>
              </>
            )}
            <Button variant="outline" onClick={handleTest}>
              <TestTube className="h-4 w-4 mr-2" />
              Test connessione
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { void loadStatus(); void loadConfig(); }}>
              Aggiorna stato
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
